import {
  getCurrentAccount,
  useCurrentAccount,
} from '@/store/use-account-store';
import { useMarketStore } from '@/store/use-markets-store';
import {
  bigIntToDecimalStr,
  bigIntToNum,
  formatNumber,
} from '@/utils/value-format';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { useTranslation } from 'react-i18next';
import TooltipWrapper from '@/components/ui/tooltip';

type Props = {
  symbol: string;
};

export default function LiqPrice({ symbol }: Props) {
  const liqPrice = useCurrentAccount(
    (state) => state.positions[symbol]?.liqPrice,
  );
  const { markPrice, priceDecimals } = useStore(
    useMarketStore(symbol),
    useShallow((state) => ({
      markPrice: state.marketData.markPrice,
      priceDecimals: state.marketSpec.priceDecimals,
    })),
  );
  if (!liqPrice?.bigint) {
    return <span className="text-muted-foreground">{'N/A'}</span>;
  }
  return (
    <span className="text-red">
      {formatNumber(liqPrice.decimal, {
        style: 'currency',
        digits: Number(priceDecimals),
      })}{' '}
      (
      {formatNumber(bigIntToDecimalStr(markPrice, priceDecimals), {
        style: 'currency',
        digits: Number(priceDecimals),
      })}
      )
    </span>
  );
}

export function LiqPriceHeader() {
  const { t } = useTranslation();
  return (
    <TooltipWrapper
      content={
        <>
          {t('positionsTable.liqPriceTooltip1')}
          <br />
          <br />
          {t('positionsTable.liqPriceTooltip2')}
        </>
      }
      className="text-wrap font-sans text-sm normal-case tracking-wide text-foreground"
      direction="bottom"
      offset={1.4}
    >
      <span className="underline">{t('positionsTable.liqPriceMark')}</span>
    </TooltipWrapper>
  );
}

export const liqPriceAccessorFn = (symbol: string) => {
  const position = getCurrentAccount((state) => state.positions[symbol]);
  if (!position) return undefined;
  const { liqPrice } = position;
  const {
    marketSpec: { priceDecimals },
  } = useMarketStore(symbol).getState();

  const liqPriceNum = bigIntToNum(liqPrice.bigint, priceDecimals);

  return Number.isNaN(liqPriceNum) ? undefined : liqPriceNum;
};
